"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlLineFlatCapGraphicsComponent = void 0;
const tslib_1 = require("tslib");
const rc_js_util_1 = require("rc-js-util");
const i_gl_indexed_point2d_binder_1 = require("../indexed-point-2d/i-gl-indexed-point2d-binder");
const core_1 = require("@visualization-tools/core");
/**
 * @public
 * Draws line caps, to be used with {@link GlCaplessLineGraphicsComponent}. Handles sizes and colors per point.
 */
class GlLineFlatCapGraphicsComponent {
    constructor(cameraBinder, indexedBinder) {
        this.cameraBinder = cameraBinder;
        this.indexedBinder = indexedBinder;
        this.type = core_1.EGraphicsComponentType.Entity;
        DEBUG_MODE && rc_js_util_1._Debug.runBlock(() => {
            (0, core_1.assertBinder)(indexedBinder, i_gl_indexed_point2d_binder_1.IndexedPoint2dIdentifier);
            rc_js_util_1._Debug.assert(indexedBinder.pointsBound === 3, "requires 3 point to be bound");
        });
        this.specification = core_1.GlProgramSpecification.mergeProgramSpecifications([
            cameraBinder.specification,
            indexedBinder.specification,
            caplessProgramSpecification,
        ]);
        this.bindings = GlLineFlatCapGraphicsComponent.getBindings();
        this.transform = core_1.GlTransformProvider.createOne(this, this.indexedBinder, (updateArg) => updateArg.userTransform, (entity) => entity);
    }
    getCacheId() {
        return [
            "GlFlatCapLine",
            this.cameraBinder.getBinderId(),
            this.indexedBinder.getBinderId(),
        ].join("_");
    }
    getLinkableBinders() {
        return [this.indexedBinder];
    }
    initialize(componentRenderer) {
        this.cameraBinder.initialize(componentRenderer);
        this.indexedBinder.initialize(componentRenderer);
        this.bindings.capGeometryAttribute.initialize(componentRenderer);
        this.bindings.canvasToClipUniform.initialize(componentRenderer);
    }
    onBeforeUpdate(componentRenderer, updateArg) {
        this.bindings.capGeometryAttribute.bindArray(componentRenderer, componentRenderer.context.STATIC_DRAW);
        const frameId = componentRenderer.sharedState.frameCounter;
        this.bindings.canvasToClipUniform.setData(updateArg.canvasDimensions.pixelToClipSize, frameId);
        this.bindings.canvasToClipUniform.bind(componentRenderer);
    }
    update(entity, componentRenderer, updateArg) {
        const length = entity.data.getLength();
        if (length < 3) {
            // nothing to draw
            return;
        }
        this.cameraBinder.update(this.cameraBinder.getBinderData(entity, updateArg, componentRenderer), componentRenderer);
        // draw 1st set
        this.indexedBinder.updateInstanced(entity, componentRenderer, 1);
        this.indexedBinder.overrideColors(componentRenderer, entity, entity.changeId);
        componentRenderer.drawInstancedArrays(componentRenderer.context.TRIANGLE_STRIP, 0, 4, (length * 0.3333333333333333) | 0);
        const startIndex = entity.data.getStart();
        // draw 2nd set
        this.indexedBinder.setPointers(startIndex + 1, entity.data.getBlockByteSize());
        this.indexedBinder.bindInstanced(componentRenderer, 1);
        componentRenderer.drawInstancedArrays(componentRenderer.context.TRIANGLE_STRIP, 0, 4, ((length - 1) * 0.3333333333333333) | 0);
        // draw 3nd set
        this.indexedBinder.updatePointers(entity);
        this.indexedBinder.setPointers(startIndex + 2, entity.data.getBlockByteSize());
        this.indexedBinder.bindInstanced(componentRenderer, 1);
        componentRenderer.drawInstancedArrays(componentRenderer.context.TRIANGLE_STRIP, 0, 4, ((length - 2) * 0.3333333333333333) | 0);
    }
    static getBindings() {
        const trianglePosition = new core_1.GlFloatAttribute("caplessLine_trianglePosition", new core_1.GlFloatBuffer(new Float32Array([
            1, 0,
            0, 0,
            1, 1,
            0, 1,
        ])), 2);
        return {
            capGeometryAttribute: trianglePosition,
            canvasToClipUniform: new core_1.GlMat3Uniform("caplessLine_canvasToClipSize", new rc_js_util_1.Mat3.f32()),
        };
    }
}
(0, tslib_1.__decorate)([
    rc_js_util_1.Once
], GlLineFlatCapGraphicsComponent.prototype, "getCacheId", null);
exports.GlLineFlatCapGraphicsComponent = GlLineFlatCapGraphicsComponent;
// language=GLSL prefix="#if __VERSION__ >=300 && __VERSION__ < 400 \n #define ATTRIBUTE in \n #define VARYING out \n #else \n #define ATTRIBUTE attribute \n #define VARYING varying \n #endif"
const vertexShaderPartial = new core_1.GlShader(
// @formatter:off
`
/* === capless line === */
ATTRIBUTE lowp vec2 caplessLine_trianglePosition;
uniform highp mat3 caplessLine_canvasToClipSize;
VARYING lowp vec4 caplessLine_lineColor;

vec2 caplessLine_getAdjustedPoistion(vec2 p1, vec2 p2, vec2 p3, float p2Size)
{
    vec2 l1Normal = generate2dLineNormal(p1, p2, vec2(1));
    vec2 l2Normal = generate2dLineNormal(p2, p3, vec2(1));
    vec2 normal = mix(l2Normal, l1Normal, caplessLine_trianglePosition.y);

    float xSign = caplessLine_trianglePosition.x * 2. - 1.;
    vec2 pixelWidthOffset = xSign * p2Size * normal;

    return p2 + mat3MultiplyVec2(caplessLine_canvasToClipSize, pixelWidthOffset);
}

void main()
{
    vec3 p1 = v_2dCamera_getClipspaceCoords2d(pointConnector_getPosition());
    vec3 p2 = v_2dCamera_getClipspaceCoords2d(pointConnector_getPosition1());
    vec3 p3 = v_2dCamera_getClipspaceCoords2d(pointConnector_getPosition2());
    float p2Size =  pointConnector_getSize1() * 0.5;

    gl_Position = vec4(caplessLine_getAdjustedPoistion(p1.xy, p2.xy, p3.xy, p2Size), p1.z, 1);
    
    vec4 p2Color = pointConnector_getColor1();
    caplessLine_lineColor = p2Color;
}
`);
// language=GLSL prefix="#if __VERSION__ >=300 && __VERSION__ < 400 \n #define VARYING in \n #define TEXTURE2D texture \n #else \n #define VARYING varying \n #define TEXTURE2D texture2D \n #endif \n void setFragmentColor(in lowp vec4 color);"
const fragmentShaderSource = new core_1.GlShader(
// @formatter:off
`
    VARYING highp vec4 caplessLine_lineColor;

    void main()
    {
        setFragmentColor(caplessLine_lineColor);
    }
    `);
const caplessProgramSpecification = new core_1.GlProgramSpecification(core_1.GlShader.combineShaders([
    core_1.generate2LinedNormalShader,
    core_1.mat3MultiplyVec2Shader,
    vertexShaderPartial,
]), fragmentShaderSource, ["ANGLE_instanced_arrays"]);
//# sourceMappingURL=gl-line-flat-cap-graphics-component.js.map